import { PcsSizingEngineResponse } from '../models/pcs-sizing-engine-response.model';
import { VCPM } from '../models/project.model';

export function getOwnedStorageClusters(data: VCPM.ProjectData | undefined, domainId: number | string | undefined): VCPM.StorageCluster[] {
  return domainId == null ? [] : (data?.clusterConfigurations ?? []).filter(c => String(c.workloadDomainId) === String(domainId));
}

export function getSonDeletionBlockers(data: VCPM.ProjectData | undefined, domainId: number | string | undefined) {
  return getOwnedStorageClusters(data, domainId).flatMap(cluster => {
    const domains = (data?.workloadDomains ?? []).filter(d => String(d.id) !== String(domainId) &&
      d.workloadList?.some(w => w.storageCluster?.id === cluster.id));
    return domains.length ? [{ cluster, domains }] : [];
  });
}

export function getSonDeletionMessage(data: VCPM.ProjectData | undefined, domainId: number | string | undefined): string {
  const blockers = getSonDeletionBlockers(data, domainId);
  return blockers.length ? `This domain owns Storage Clusters used by other domains: ${blockers.map(b =>
    `${b.cluster.name} (${b.domains.map(d => d.domainName).join(', ')})`).join('; ')}. This domain will be deleted automatically once it has no workloads and no other domain uses any of its Storage Clusters.` : '';
}

export function isRetainedSonOwner(data: VCPM.ProjectData, domain: VCPM.WorkloadDomain): boolean {
  return !domain.workloadList?.length && getSonDeletionBlockers(data, domain.id).length > 0;
}

/** Mutates a transaction's cloned project data, never the live state. */
export function collectOrphanedClustersAndOwners(data: VCPM.ProjectData): boolean {
  const ownerIds = new Set((data.clusterConfigurations ?? []).map(c => String(c.workloadDomainId)));
  const compute = new Set<string>();
  const storage = new Set<string>();
  for (const domain of data.workloadDomains ?? []) {
    for (const workload of domain.workloadList ?? []) {
      if (workload.computeCluster?.id) compute.add(workload.computeCluster.id);
      if (workload.storageCluster?.id) storage.add(workload.storageCluster.id);
    }
  }
  let changed = false;
  for (const domain of data.workloadDomains ?? []) {
    const remaining = domain.clusterConfigurations?.filter(c => compute.has(c.id));
    changed ||= remaining?.length !== domain.clusterConfigurations?.length;
    domain.clusterConfigurations = remaining;
  }
  const storageClusters = data.clusterConfigurations?.filter(c => storage.has(c.id));
  changed ||= storageClusters?.length !== data.clusterConfigurations?.length;
  data.clusterConfigurations = storageClusters;
  const domains = data.workloadDomains?.filter(d => d.workloadList?.length || !ownerIds.has(String(d.id)) ||
    getOwnedStorageClusters(data, d.id).length > 0);
  changed ||= domains?.length !== data.workloadDomains?.length;
  data.workloadDomains = domains;
  if (changed) data.output = undefined;
  return changed;
}

/** UI-only domain shells; never add these to the engine response or request. */
export function getOutputDomainRecommendations(project: VCPM.Project | undefined): PcsSizingEngineResponse.Recommendation[] {
  const recommendations = [...(project?.data.output?.recommendation?.workloadDomainsRecommendation?.result?.recommendations ?? [])];
  for (const domain of project?.data.workloadDomains ?? []) {
    if (!recommendations.some(r => String(r.requirementsId) === String(domain.id)) &&
      getSonDeletionBlockers(project?.data, domain.id).length) {
      recommendations.push({ requirementsId: String(domain.id), requirementsName: domain.domainName,
        workloadDomainType: domain.domainType, workloadRecommendations: [], type: 'WORKLOAD',
        recommendationId: '', components: [], errors: [], domainSummary: {
          numberOfVMs: 0, numberOfHosts: undefined, numberOfRacks: undefined, numberOfClusters: undefined,
          numberofTorSwitch: undefined, numberOfInterRackSwitch: undefined, numberOfManagementSwitch: undefined,
        } });
    }
  }
  return recommendations;
}
